import { useState } from 'react'
import { History, Layers } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  getSystemSummary,
  otherPlatforms,
  type Service,
  type ServiceStatus,
} from '@/lib/dashboard-data'
import type { IncidentWindow } from '@/lib/platform-incidents'
import { useLiveOtherPlatforms } from '@/hooks/useLiveTiers'
import { usePlatformIncidents } from '@/hooks/useDeepDive'
import { HoverCard } from '@/components/ui/HoverCard'
import { Modal } from '@/components/ui/Modal'
import { ServiceCard } from '@/components/ui/ServiceCard'
import { StatusPill } from '@/components/ui/StatusPill'
import { SegmentedPicker } from '@/components/systems/SegmentedPicker'
import { PlatformIncidentsLog } from '@/components/systems/PlatformIncidentsLog'
import {
  PlatformIncidentSummary,
  type IncidentsQueryState,
} from '@/components/systems/PlatformIncidentSummary'

/** Trouble first, so an outage is the first thing the popup shows. */
const SEVERITY: Record<ServiceStatus, number> = {
  critical: 0,
  vendor_silent: 0,
  degraded: 1,
  unknown: 2,
  healthy: 3,
}

function worstFirst(services: Service[]): Service[] {
  return services
    .map((s, i) => ({ s, i }))
    .sort((a, b) => SEVERITY[a.s.status] - SEVERITY[b.s.status] || a.i - b.i)
    .map(({ s }) => s)
}

const KEYS = otherPlatforms.map((s) => s.systemId as string)

/** The window behind the hover cards and the list's incident column. */
const HOVER_PERIOD: IncidentWindow = '7d'

type View = 'cards' | 'list'
type Mode = 'platforms' | 'incidents'

/**
 * Navbar button for the secondary catalog platforms (`otherPlatforms`). They are
 * not on the grid, so the button carries their worst state: a coloured dot is
 * the only hint of trouble someone gets without opening it.
 */
export function OtherPlatforms() {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<Mode>('platforms')
  const [view, setView] = useState<View>('cards')
  const platforms = useLiveOtherPlatforms()
  // Fetched only while the popup is open: one request per platform.
  const incidents = usePlatformIncidents(KEYS, HOVER_PERIOD, open)
  const incidentsFor = (s: Service): IncidentsQueryState | undefined =>
    incidents[KEYS.indexOf(s.systemId as string)]

  const summary = getSystemSummary([{ id: 'other', label: 'Other Platforms', services: platforms }])
  const down = summary.critical + summary.noFeed
  const counters = [
    { value: summary.healthy, label: 'healthy', dot: 'bg-healthy' },
    { value: summary.degraded, label: 'degraded', dot: 'bg-degraded' },
    { value: summary.critical, label: 'critical', dot: 'bg-critical-bright' },
    { value: summary.noFeed, label: 'down', dot: 'bg-critical-bright' },
  ]
  const close = () => {
    setOpen(false)
    setMode('platforms')
  }
  const hover = (s: Service) => (
    <PlatformIncidentSummary name={s.name} period={HOVER_PERIOD} query={incidentsFor(s)} />
  )

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        title={`${summary.total} more platforms the worker checks`}
        className={cn(
          'flex h-9 shrink-0 items-center gap-2 rounded-lg border bg-transparent px-3 text-sm font-medium transition-colors',
          open
            ? 'border-line-bright text-fg'
            : 'border-line text-fg-muted hover:border-line-bright hover:text-fg',
        )}
      >
        <Layers className="size-4" />
        <span className="hidden sm:inline">Other Platforms</span>
        <span className="bg-surface-3 text-fg flex items-center gap-1 rounded-full px-1.5 font-mono text-[10px] font-bold">
          {(down > 0 || summary.degraded > 0) && (
            <span
              data-testid="other-platforms-alert"
              className={cn(
                'size-1.5 rounded-full',
                down > 0 ? 'bg-critical-bright' : 'bg-degraded',
              )}
            />
          )}
          {summary.total}
        </span>
      </button>

      <Modal
        open={open}
        onClose={close}
        title="Other Platforms"
        subtitle={
          mode === 'platforms'
            ? 'Secondary platforms the background worker also checks'
            : 'Every incident on these platforms, newest first'
        }
        icon={
          <span className="bg-primary/15 text-primary-bright grid size-9 shrink-0 place-items-center rounded-full">
            <Layers className="size-5" />
          </span>
        }
        actions={
          <button
            type="button"
            onClick={() => setMode((m) => (m === 'platforms' ? 'incidents' : 'platforms'))}
            className="border-line text-fg-muted hover:border-line-bright hover:text-fg flex h-8 shrink-0 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors"
          >
            {mode === 'platforms' ? <History className="size-4" /> : <Layers className="size-4" />}
            {mode === 'platforms' ? 'All Incidents' : 'Platforms'}
          </button>
        }
        className="max-w-5xl"
      >
        {mode === 'incidents' ? (
          <PlatformIncidentsLog platforms={otherPlatforms} />
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                {counters.map((c) => (
                  <span key={c.label} className="flex items-center gap-1.5 font-mono text-[13px]">
                    <span className={cn('size-1.5 rounded-full', c.dot)} />
                    <span className="text-fg font-semibold">{c.value}</span>
                    <span className="text-fg-muted">{c.label}</span>
                  </span>
                ))}
              </div>
              <SegmentedPicker
                value={view}
                onChange={setView}
                options={['cards', 'list'] as const}
                label="View"
              />
            </div>
            {view === 'cards' ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {worstFirst(platforms).map((service) => (
                  <HoverCard key={service.name} content={hover(service)}>
                    <ServiceCard service={service} size="sm" className="h-full" />
                  </HoverCard>
                ))}
              </div>
            ) : (
              <PlatformList
                platforms={worstFirst(platforms)}
                incidentsFor={incidentsFor}
                hover={hover}
              />
            )}
          </>
        )}
      </Modal>
    </>
  )
}

/** Columns from md up; below that a row is name + status, and the hover carries the rest. */
const ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 md:grid-cols-[minmax(0,1.1fr)_6.5rem_minmax(0,2fr)_4.5rem_5.5rem_4.5rem]'

function PlatformList({
  platforms,
  incidentsFor,
  hover,
}: {
  platforms: Service[]
  incidentsFor: (s: Service) => IncidentsQueryState | undefined
  hover: (s: Service) => React.ReactNode
}) {
  return (
    <div className="border-line divide-line divide-y rounded-lg border">
      <div
        className={cn(
          ROW_GRID,
          'text-fg-subtle hidden px-3 py-2 font-mono text-[10px] tracking-wider uppercase md:grid',
        )}
      >
        <span>Platform</span>
        <span>Status</span>
        <span>What we see</span>
        <span className="text-right">Response</span>
        <span className="text-right">Incidents 7d</span>
        <span className="text-right">Updated</span>
      </div>
      {platforms.map((s) => {
        const query = incidentsFor(s)
        const page = query?.data
        return (
          <HoverCard key={s.name} content={hover(s)} className="rounded-none">
            <div className={cn(ROW_GRID, 'hover:bg-surface-3/40 px-3 py-2.5 transition-colors')}>
              <div className="min-w-0">
                <p className="text-fg truncate text-sm font-semibold">{s.name}</p>
                <p className="text-fg-muted truncate font-mono text-[11px]">{s.vendor}</p>
              </div>
              <StatusPill status={s.status} className="justify-self-end md:justify-self-start" />
              <p className="text-fg-muted hidden truncate font-mono text-[11px] md:block">
                {s.detail ?? s.note ?? '—'}
              </p>
              <p className="text-fg hidden text-right font-mono text-xs md:block">{s.metric}</p>
              <p
                className={cn(
                  'hidden text-right font-mono text-xs md:block',
                  page?.total ? 'text-fg' : 'text-fg-subtle',
                )}
              >
                {query?.isError ? '—' : page ? page.total : '…'}
              </p>
              <p className="text-fg-subtle hidden text-right font-mono text-[11px] md:block">
                {s.updated}
              </p>
            </div>
          </HoverCard>
        )
      })}
    </div>
  )
}
